
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { RefreshCw, CheckCircle, XCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Database } from "@/integrations/supabase/types";

// Use the type from the database schema
type SupportedCurrency = Database['public']['Enums']['supported_currency'];

interface Currency {
  currency_code: SupportedCurrency;
  display_name: string;
  symbol: string;
  exchange_rate: number;
  active: boolean;
  last_updated: string;
}

export default function Currencies() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lastUpdateStatus, setLastUpdateStatus] = useState<{
    last_update_completed?: string;
    status?: string;
    error_message?: string;
  }>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchCurrencies();
    fetchUpdateStatus();
  }, []);

  const fetchUpdateStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("system_settings")
        .select("setting_value")
        .eq("setting_key", "currency_update_status")
        .single();

      if (error) {
        console.error("Error fetching update status:", error);
        return;
      }

      if (data && data.setting_value) {
        // Parse JSON if it's a string or use the object directly
        const settingValue = typeof data.setting_value === 'string' 
          ? JSON.parse(data.setting_value) 
          : data.setting_value as { 
              last_update_completed?: string; 
              status?: string;
              error_message?: string;
            };
        
        setLastUpdateStatus(settingValue);
      }
    } catch (error) {
      console.error("Error in fetchUpdateStatus:", error);
    }
  };

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("currencies")
        .select("*")
        .order("currency_code");

      if (error) {
        toast({
          title: "Error fetching currencies",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setCurrencies(data || []);
      }
    } catch (error: any) {
      toast({
        title: "Error fetching currencies",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCurrencyStatus = async (code: SupportedCurrency, active: boolean) => {
    try {
      const { error } = await supabase
        .from("currencies")
        .update({ active })
        .eq("currency_code", code);

      if (error) {
        toast({
          title: "Error updating currency status",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Currency updated",
          description: `${code} is now ${active ? "active" : "inactive"}`,
        });
        fetchCurrencies();
      }
    } catch (error: any) {
      toast({
        title: "Error updating currency status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateExchangeRate = async (code: SupportedCurrency, rate: number) => {
    try {
      const { error } = await supabase
        .from("currencies")
        .update({ 
          exchange_rate: rate,
          last_updated: new Date().toISOString() 
        })
        .eq("currency_code", code);

      if (error) {
        toast({
          title: "Error updating exchange rate",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Exchange rate updated",
          description: `${code} exchange rate updated to ${rate}`,
        });
        fetchCurrencies();
      }
    } catch (error: any) {
      toast({
        title: "Error updating exchange rate",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateAllCurrencies = async () => {
    setUpdating(true);
    try {
      // Call the edge function to update all currency rates
      const { data, error } = await supabase.functions.invoke("update-currency-rates");
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to send a request to the Edge Function");
      }
      
      toast({
        title: "Currency rates update initiated",
        description: "The exchange rates are being updated from external providers",
      });
      
      // Fetch the updated currencies after a short delay
      setTimeout(() => {
        fetchCurrencies();
        fetchUpdateStatus();
        setUpdating(false);
      }, 3000);
    } catch (error: any) {
      console.error("Update currencies error:", error);
      toast({
        title: "Error updating currencies",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setUpdating(false);
    }
  };

  // Use proper currency type
  const CurrencyRow = ({ currency }: { currency: Currency }) => {
    const form = useForm({
      defaultValues: {
        rate: currency.exchange_rate.toString(),
      },
    });

    const onSubmit = (data: { rate: string }) => {
      updateExchangeRate(currency.currency_code, parseFloat(data.rate));
    };

    return (
      <TableRow key={currency.currency_code}>
        <TableCell className="font-medium">
          <div className="flex items-center">
            <span className="mr-2">{currency.symbol}</span>
            {currency.currency_code}
          </div>
        </TableCell>
        <TableCell>{currency.display_name}</TableCell>
        <TableCell>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex space-x-2">
              <FormField
                control={form.control}
                name="rate"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Input {...field} type="number" step="0.0001" className="w-24" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button type="submit" size="sm" variant="outline">Save</Button>
            </form>
          </Form>
        </TableCell>
        <TableCell>
          <Switch 
            checked={currency.active} 
            onCheckedChange={(checked) => updateCurrencyStatus(currency.currency_code, checked)}
          />
        </TableCell>
        <TableCell>
          {currency.active ? 
            <CheckCircle className="h-5 w-5 text-green-500" /> : 
            <XCircle className="h-5 w-5 text-red-500" />
          }
        </TableCell>
        <TableCell className="text-muted-foreground text-sm">
          {currency.last_updated ? new Date(currency.last_updated).toLocaleString() : "Never"}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Currency Management</h1>
            <p className="text-muted-foreground">
              Manage currency exchange rates and availability
            </p>
          </div>
          <Button 
            onClick={updateAllCurrencies}
            disabled={updating}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${updating ? "animate-spin" : ""}`} />
            {updating ? "Updating..." : "Update All Rates"}
          </Button>
        </div>

        {lastUpdateStatus.last_update_completed && (
          <div className={`mb-4 p-4 rounded-lg ${lastUpdateStatus.status === 'error' ? 'bg-red-100' : 'bg-muted'}`}>
            <p className="text-sm">
              Last automated update: {new Date(lastUpdateStatus.last_update_completed).toLocaleString()}
              {lastUpdateStatus.status && (
                <span className="ml-2">
                  Status: <span className={lastUpdateStatus.status === 'success' ? 'text-green-500' : 'text-red-500'}>
                    {lastUpdateStatus.status}
                  </span>
                </span>
              )}
              {lastUpdateStatus.status === 'error' && lastUpdateStatus.error_message && (
                <span className="block text-red-500 mt-1">
                  Error: {lastUpdateStatus.error_message}
                </span>
              )}
            </p>
          </div>
        )}

        <div className="bg-card rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Exchange Rate (1 USD =)</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[80px]">Active</TableHead>
                <TableHead>Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    Loading currencies...
                  </TableCell>
                </TableRow>
              ) : currencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No currencies found
                  </TableCell>
                </TableRow>
              ) : (
                currencies.map((currency) => (
                  <CurrencyRow key={currency.currency_code} currency={currency} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
}
